// src/components/IdeaCapture.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import IdeaForm from './IdeaForm';
import { encryptIdea } from '../utils/encryption';

function IdeaCapture({ setHasUnsavedChanges }) {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    idea: '',
    description: '',
    tags: [],
    status: 'Drafted',
    rating: '0',
    subtasks: [],
    lessonsLearned: '',
    resources: [],
    color: null
  });

  const handleChange = (updatedData) => {
    setFormData(updatedData);
    setHasUnsavedChanges(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      alert('Please sign in to create an idea');
      return;
    }

    try {
      // Encrypt the idea data before saving
      const encryptedData = encryptIdea({
        idea: formData.idea.trim(),
        description: formData.description.trim(),
        tags: formData.tags || [],
        status: formData.status || 'Drafted',
        rating: formData.rating || '0',
        subtasks: formData.subtasks || [],
        lessonsLearned: formData.lessonsLearned || '',
        resources: formData.resources || [],
        color: formData.color || null
      }, process.env.REACT_APP_ENCRYPTION_KEY);

      // Add the idea to Firestore
      const ideasRef = collection(db, 'users', user.uid, 'ideas');
      await addDoc(ideasRef, {
        ...encryptedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setHasUnsavedChanges(false);
      navigate('/');
    } catch (error) {
      console.error('Error creating idea:', error);
      alert('Failed to create idea. Please try again.');
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      setHasUnsavedChanges(false);
      navigate('/');
    }
  };

  const isNewEntry = !formData.idea;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4 max-w-4xl mx-auto">
      {isNewEntry && (
        <div className="mb-6 text-center">
          <h2 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">
            Your vault is waiting.
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Capture your first breakthrough below to get started.
          </p>
        </div>
      )}
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
        {isNewEntry ? 'Start your vault' : 'Add New Idea'}
      </h2>
      <IdeaForm
        formData={formData}
        setFormData={handleChange}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        autoFocus={true}
      />
    </div>
  );
}

export default IdeaCapture;

